import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { SearchContent } from './components/SearchContent';
import { SearchHeader } from './components/SearchHeader';
import { SiteSearchProvider } from './contexts/SiteSearchContext';
import styles from './SiteSearch.module.scss';

/**
 *
 * The SiteSearch Search Page is the main global search page
 * for finding content across the site.
 * @param {object} props - The component props
 * @param {array} props.breadcrumbs - The breadcrumb items
 * @param {object} props.coveo - The Coveo search engine configuration
 * @param {object} props.errorComponent - The search server error fragment
 * @param {object} props.fixedRecommendedCards - The fixed recommended cards
 * @param {object} props.illustration - The illustration to display in the hero
 * @param {object} props.noResultsComponent - The no results fragment
 * @param {object} props.paginationButton - The pagination button
 * @param {object} props.validationError - The validation error message
 * @param {string} props.className - Additional class name
 * @param {string} props.searchLabel - The input search label
 * @param {string} props.title - The page title
 * @returns {React.Component} The default search page component
 */
const SiteSearch = ({
    breadcrumbs,
    className,
    id,
    coveo,
    errorComponent,
    fixedRecommendedCards,
    illustration,
    noResultsComponent,
    paginationButton,
    searchLabel,
    title,
    validationError,
    enableRecommendedResults,
    ...props
}) => {
    const [isRendered, setIsRendered] = React.useState(false);

    useEffect(() => {
        if (!isRendered) {
            setIsRendered(true);
        }
    }, [isRendered, setIsRendered]);

    // render the SiteSearch as a client component
    return isRendered ? (
        <SiteSearchProvider
            coveo={coveo}
            fixedRecommendedCards={fixedRecommendedCards}
            enableRecommendedResults={enableRecommendedResults}
        >
            <section className={classnames(styles['site-search'], className)} {...props}>
                {/* Search Header */}
                <SearchHeader
                    breadcrumbs={breadcrumbs}
                    searchLabel={searchLabel}
                    title={title}
                    validationError={validationError}
                    illustration={illustration}
                />

                {/* Search Content */}
                <SearchContent
                    className={styles['site-search__content']}
                    title={title}
                    errorComponent={errorComponent}
                    noResultsComponent={noResultsComponent}
                    paginationButton={paginationButton}
                />
            </section>
        </SiteSearchProvider>
    ) : null;
};

SiteSearch.propTypes = {
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            title: PropTypes.string,
            csr: PropTypes.bool,
        }),
    ),
    className: PropTypes.string,
    errorComponent: PropTypes.node,
    fixedRecommendedCards: PropTypes.shape({
        [PropTypes.string]: PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string,
            isInternal: PropTypes.bool,
            link: PropTypes.shape({
                href: PropTypes.string,
                ariaLabel: PropTypes.string,
            }),
        }),
    }),
    illustration: PropTypes.shape({
        scene: PropTypes.string,
        alignment: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        excludes: PropTypes.arrayOf(PropTypes.string),
        autoplay: PropTypes.bool,
        loop: PropTypes.bool,
    }),
    noResultsComponent: PropTypes.node,
    paginationButton: PropTypes.shape({
        label: PropTypes.string,
        ariaLabel: PropTypes.string,
    }),
    searchLabel: PropTypes.string,
    title: PropTypes.string,
    validationError: PropTypes.string,
};

SiteSearch.defaultProps = {
    breadcrumbs: [],
    fixedRecommendedCards: {},
    illustration: null,
    paginationButton: {
        label: 'More Results',
        ariaLabel: 'View more search results',
    },
    searchLabel: 'Search',
    title: 'Search',
    validationError: 'Please enter at least one search query',
};

export default SiteSearch;
